import React, { useState } from 'react';
import { TELL_ANTHONY } from '../../../../services/trackJobsModel';
import { buildSongMeta, printLyrics } from '../songDisplay';
import LyricsWithSections from '../sheets/LyricsWithSections';
import PopoutWindow from '../../../../components/PopoutWindow';
import { LYRICS_WINDOW_NAME, LYRICS_WINDOW_PATH } from './lyricsWindow';
import NowPlayingBar from './NowPlayingBar';
import JotBar from '../notes/JotBar';
import bird from '../../../../assets/stageBird.svg';
import './LyricsPane.css';
import './StageLook.css';

/**
 * Lyrics mode of the Song panel (Lesson Dashboard pass 3 D4, 2026-09-28).
 * Moved verbatim from RightColumn, where it was the column's only content:
 * the Find / Edit / Print actions, then the lyrics (section labels as
 * buttons that share the chips' state), the editor, or "No lyrics yet" with
 * **Find them** / **paste them**. The panel's own header carries the title,
 * so this row carries the actions alone. At every width now: the same pane
 * renders in the desktop column and in the phone / iPad sheet.
 */
/**
 * What the words are following, in one line over them (pass 4 S2.2): a track
 * that is playing, timing from the original recording (and whether a studio
 * Mac has checked it by ear), or no timing at all.
 */
export const followLine = ({ follow, lyricsData }) => {
	if (follow && follow.source === 'video') return { icon: 'fa-video', text: follow.playing ? 'Following the lyric video' : 'Lyric video paused', small: 'tap a line to play from it', on: follow.playing };
	if (follow && follow.playing && follow.currentTime != null) return { icon: 'fa-volume-high', text: 'Following the track', small: 'tap a line to play from it', on: true };
	const timing = lyricsData && lyricsData.timing;
	if (lyricsData && Array.isArray(lyricsData.timed) && lyricsData.timed.length) {
		return {
			icon: 'fa-clock',
			text: 'Timed to the original recording',
			small: timing && timing.checked ? 'checked by ear on the studio Mac' : 'not yet checked by ear',
		};
	}
	if (follow) return { icon: 'fa-clock', text: 'Timed from a track', small: 'tap a line to play from it' };
	if (lyricsData && lyricsData.verified) return { icon: 'fa-circle-check', text: 'Words checked by a teacher', small: 'no timing yet' };
	return { icon: 'fa-align-left', text: 'Words found, no timing yet', small: 'they follow once a track exists' };
};

/**
 * `detach` (2026-10-05, desktop only): { detached, onDetach, onAttach }. With
 * it the actions row carries a control that opens this pane in a window of
 * its own (/lyrics-window, through PopoutWindow: the SAME instance, portaled,
 * so the follow highlight, tap-to-seek, edit and Wrong lyrics all keep
 * working there), and the column shows a line saying so with a way back.
 * Closing that window brings the pane back by itself. Anthony: "I want to be
 * able to detach the scrolling lyric control column."
 */
/**
 * `nowPlaying` (2026-10-29): { file, files, player, onSaveKey, saveKeyWho }.
 * With a file, the pane becomes Now Playing: the words scroll above a player
 * pinned to the bottom (NowPlayingBar), and detaching takes both, so the
 * window is the music-app view. Anthony: "combine the player and the
 * scrolling lyrics".
 */
/**
 * Text size (2026-10-30, Anthony: "lets add text sizing controls"): A− / A+
 * in the actions row step through LYRIC_SIZES. One size docked and one
 * detached, since the window is read from further away; each is remembered on
 * this device (a convenience, so storage failing just means the default).
 */
export const LYRIC_SIZES = [14, 15, 17, 19, 21, 24, 28, 32, 38];
const SIZE_DEFAULT = { docked: 17, window: 21 };
const sizeKey = (where) => `cyv.lyricsSize.${where}`;
const readSize = (where) => {
	try {
		const n = Number(window.localStorage.getItem(sizeKey(where)));
		if (LYRIC_SIZES.includes(n)) return n;
	} catch (e) {
		/* storage blocked: use the default */
	}
	return SIZE_DEFAULT[where];
};
const useLyricSize = (where) => {
	const [sizes, setSizes] = useState(() => ({ docked: readSize('docked'), window: readSize('window') }));
	const size = sizes[where];
	const at = LYRIC_SIZES.indexOf(size);
	const step = (d) => {
		const next = LYRIC_SIZES[Math.min(LYRIC_SIZES.length - 1, Math.max(0, at + d))];
		setSizes((prev) => ({ ...prev, [where]: next }));
		try {
			window.localStorage.setItem(sizeKey(where), String(next));
		} catch (e) {
			/* storage blocked: the size holds for this visit */
		}
	};
	return { size, canDown: at > 0, canUp: at < LYRIC_SIZES.length - 1, down: () => step(-1), up: () => step(1) };
};

/**
 * `jot` (2026-10-30): { onSubmit, onFirstJot, placeholder }. In the detached
 * window only, the song-note field sits under the player, so a note can be
 * written without going back to the lesson tab. Anthony: "embed, underneath
 * the player at the bottom, the song note field". It files exactly as the
 * lesson's own jot bar does (same handler, the active section attached).
 */
const LyricsPane = ({ song, lyrics, sections = null, activeSectionId = null, onSelectSection = null, follow = null, setup = null, listening = false, detach = null, nowPlaying = null, jot = null }) => {
	const title = song?.metadata?.title || song?.name || 'Untitled song';
	const detached = Boolean(detach && detach.detached);
	const textSize = useLyricSize(detached ? 'window' : 'docked');
	// The browser refused the window (a pop-up blocker): say so in the column.
	const [blocked, setBlocked] = useState(false);
	const meta = buildSongMeta(song);
	const text = lyrics?.lyrics?.text || '';
	const status = lyrics?.status || 'idle';
	const [wrongOpen, setWrongOpen] = useState(false);
	const line = text ? followLine({ follow, lyricsData: lyrics?.lyrics }) : null;
	const check = lyrics?.lyrics?.check || null;

	const hasBar = Boolean(nowPlaying && nowPlaying.file && nowPlaying.player);
	const jotField =
		detached && jot ? (
			<div className="ld-lyrics__jot">
				<span className="ld-lyrics__jotlabel">Song note</span>
				<JotBar onSubmit={jot.onSubmit} onFirstJot={jot.onFirstJot || null} placeholder={jot.placeholder || 'Note on this song'} />
			</div>
		) : null;
	const pane = (
		<div className={`ld-rightcol__lyrics ld-stagelook${hasBar || jotField ? ' has-np' : ''}`} style={{ '--ld-lyrics-size': `${textSize.size}px` }}>
			{/* The Stage's bird, behind the scrolling words (StageLook.css). Decorative. */}
			<div className="ld-stagelook__birdwrap" aria-hidden="true">
				<div className="ld-stagelook__bird" style={{ '--ld-bird': `url(${bird})` }} />
			</div>
			<div className="ld-rightcol__lyricshead ld-rightcol__lyricshead--actions">
				{/* Find lyrics — DESKTOP'S TRIGGER FOR A FETCH THAT ALREADY
							    EXISTED. useLyrics.load() has always been able to fetch
							    from LyricsService, but its only caller was LyricsSheet
							    (the phone sheet), which fires it automatically on mount.
							    This column received the whole `lyrics` object — load and
							    all — and never called it, so on desktop the panel said
							    "No lyrics yet" with no way to get any except pasting.

							    EXPLICIT BUTTON RATHER THAN THE SHEET'S AUTO-FETCH, on
							    purpose: the sheet is opened deliberately to look at
							    lyrics, whereas this column is simply present during a
							    lesson — auto-fetching would fire a network call on every
							    song switch and can drop an error line into the panel
							    mid-lesson. Teacher asks, then it goes.

							    Hidden once lyrics exist because `load()` early-returns on
							    a song that already has text: a visible control that
							    silently does nothing is worse than no control. Re-fetch
							    is therefore not offered — clear the lyrics with the pencil
							    first. */}
				{!text && (
					<button
						type="button"
						className="ld-rightcol__lyricsicon"
						aria-label="Find lyrics"
						disabled={lyrics?.isLoading}
						onClick={() => lyrics?.load?.()}>
						<i className="fa-sharp fa-solid fa-magnifying-glass" aria-hidden="true" />
					</button>
				)}
				{text && (
					<>
						<button
							type="button"
							className="ld-rightcol__lyricsicon ld-lyrics__size ld-lyrics__size--down"
							aria-label="Smaller text"
							title="Smaller text"
							disabled={!textSize.canDown}
							onClick={() => textSize.canDown && textSize.down()}>
							A−
						</button>
						<button
							type="button"
							className="ld-rightcol__lyricsicon ld-lyrics__size ld-lyrics__size--up"
							aria-label="Larger text"
							title="Larger text"
							disabled={!textSize.canUp}
							onClick={() => textSize.canUp && textSize.up()}>
							A+
						</button>
					</>
				)}
				<button
					type="button"
					className="ld-rightcol__lyricsicon"
					aria-label={lyrics?.editMode ? 'Stop editing' : 'Edit lyrics'}
					aria-pressed={lyrics?.editMode}
					onClick={() => lyrics?.setEditMode((v) => !v)}>
					<i className="fa-sharp fa-solid fa-pen" aria-hidden="true" />
				</button>
				<button
					type="button"
					className="ld-rightcol__lyricsicon"
					aria-label="Print"
					disabled={!text}
					onClick={() => printLyrics(title, meta, text)}>
					<i className="fa-sharp fa-solid fa-print" aria-hidden="true" />
				</button>
				{detach && !detached && (
					<button
						type="button"
						className="ld-rightcol__lyricsicon"
						aria-label={hasBar ? 'Open Now Playing in its own window' : 'Open the lyrics in their own window'}
						title={hasBar ? 'Open Now Playing in its own window' : 'Open the lyrics in their own window'}
						onClick={() => {
							setBlocked(false);
							detach.onDetach();
						}}>
						<i className="fa-sharp fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
					</button>
				)}
				{detach && detached && (
					<button
						type="button"
						className="ld-rightcol__lyricsicon"
						aria-label={hasBar ? 'Bring Now Playing back to the lesson' : 'Bring the lyrics back to the lesson'}
						title={hasBar ? 'Bring Now Playing back to the lesson' : 'Bring the lyrics back to the lesson'}
						onClick={detach.onAttach}>
						<i className="fa-sharp fa-solid fa-arrow-down-left-and-arrow-up-right-to-center" aria-hidden="true" />
					</button>
				)}
			</div>
			{blocked && (
				<p className="ld-rightcol__status ld-rightcol__status--error" role="alert">
					The browser blocked the window. Allow pop-ups for this site, then try again.
				</p>
			)}

			{setup}
			{listening && (
				<div className="ld-lyrics__follow is-quiet" role="status">
					<i className="fa-sharp fa-solid fa-ear-listen" aria-hidden="true" />
					<span className="ld-lyrics__followtext">The studio Mac is listening to the lyric video</span>
					<small>to check these words</small>
				</div>
			)}
			{check && check.matches === false && !check.dismissed && !lyrics?.editMode && (
				<div className="ld-lyrics__check" role="alert">
					<p className="ld-lyrics__nonehead">
						<i className="fa-sharp fa-solid fa-circle-exclamation" aria-hidden="true" />
						These words don't match the lyric video.
					</p>
					<p className="ld-lyrics__detail">
						{check.unverified
							? 'The studio Mac heard different words. They are its own hearing, so read them before you use them.'
							: 'The studio Mac heard a different published version of the words, timed to the video.'}
					</p>
					<details className="ld-lyrics__heard">
						<summary>What the recording says</summary>
						<pre>{check.text}</pre>
					</details>
					<div className="ld-lyrics__acts">
						<button type="button" className="ld-lyrics__btn ld-lyrics__btn--primary" onClick={() => lyrics?.acceptHeard?.()}>
							Use the words from the recording
						</button>
						<button type="button" className="ld-lyrics__btn" onClick={() => lyrics?.keepWords?.()}>
							Keep these words
						</button>
					</div>
				</div>
			)}
			{check && check.heard === false && !check.dismissed && (
				<p className="ld-rightcol__status">The studio Mac could not hear words in the lyric video, so these words are not checked.</p>
			)}
			{status === 'fetching' && (
				<div className="ld-lyrics__follow is-quiet" role="status">
					<i className="fa-sharp fa-solid fa-spinner-third fa-spin" aria-hidden="true" />
					<span className="ld-lyrics__followtext">Finding the words and timing them</span>
					<small>a few seconds</small>
				</div>
			)}
			{status === 'failed' && !text && (
				<div className="ld-lyrics__fail" role="alert">
					<p className="ld-lyrics__failhead">
						<i className="fa-sharp fa-solid fa-triangle-exclamation" aria-hidden="true" />
						The words could not be fetched. {TELL_ANTHONY}
					</p>
					<p className="ld-lyrics__detail">{[lyrics?.failDetail, title].filter(Boolean).join(' · ')}</p>
					<div className="ld-lyrics__acts">
						<button type="button" className="ld-lyrics__btn" onClick={() => lyrics?.load?.()}>
							Try again
						</button>
						<button type="button" className="ld-lyrics__btn" onClick={() => lyrics?.setEditMode(true)}>
							Paste the words
						</button>
					</div>
				</div>
			)}
			{lyrics?.error && !lyrics.isLoading && (
				<p className="ld-rightcol__status ld-rightcol__status--error">{lyrics.error}</p>
			)}
			{line && !lyrics?.editMode && (
				<div className={`ld-lyrics__follow${line.on ? ' is-on' : ' is-quiet'}`}>
					<i className={`fa-sharp fa-solid ${line.icon}`} aria-hidden="true" />
					<span className="ld-lyrics__followtext">{line.text}</span>
					<small>{line.small}</small>
				</div>
			)}

			{lyrics?.editMode ? (
				<>
					{lyrics.replacing && <p className="ld-rightcol__status">Paste the right words. They replace the wrong ones, and their timing.</p>}
					<textarea
						className="ld-rightcol__editor"
						value={lyrics.draft}
						placeholder="Paste or type lyrics…"
						onChange={(e) => lyrics.setDraft(e.target.value)}
					/>
					<button type="button" className="ld-rightcol__save" onClick={lyrics.saveDraft}>
						Save
					</button>
				</>
			) : text ? (
				<LyricsWithSections
					className="ld-rightcol__text"
					text={text}
					sections={sections}
					activeId={activeSectionId}
					onSelect={onSelectSection}
					follow={follow}
				/>
			) : status === 'none' ? (
				<div className="ld-lyrics__none">
					<p className="ld-lyrics__nonehead">
						<i className="fa-sharp fa-solid fa-align-left" aria-hidden="true" />
						No words found for {title}.
					</p>
					<p className="ld-lyrics__detail">That happens with new or local songs. Paste the words and the sections are found from them.</p>
					<div className="ld-lyrics__acts">
						<button type="button" className="ld-lyrics__btn ld-lyrics__btn--primary" onClick={() => lyrics?.setEditMode(true)}>
							Paste the words
						</button>
					</div>
				</div>
			) : status === 'failed' || status === 'fetching' ? null : (
				!lyrics?.isLoading && (
					<p className="ld-rightcol__status">
						No lyrics yet.{' '}
						{/* Both routes named in the copy — the icon row above is
								    discoverable only if you already know to look at it,
								    and "Paste them" alone is what made the fetch read as
								    non-existent on this surface. */}
						<button
							type="button"
							className="ld-rightcol__paste"
							disabled={lyrics?.isLoading}
							onClick={() => lyrics?.load?.()}>
							Find them
						</button>
						{' or '}
						<button type="button" className="ld-rightcol__paste" onClick={() => lyrics?.setEditMode(true)}>
							paste them
						</button>
						.
					</p>
				)
			)}
			{text && !lyrics?.editMode && (
				<div className="ld-lyrics__foot">
					<span className="ld-lyrics__source">
						<i className={`fa-sharp fa-solid ${lyrics?.lyrics?.verified ? 'fa-circle-check' : 'fa-globe'}`} aria-hidden="true" />
						{lyrics?.lyrics?.verified ? 'Words pasted by a teacher' : 'Words from a lyrics site'}
					</span>
					{!wrongOpen ? (
						<button type="button" className="ld-lyrics__btn ld-lyrics__btn--quiet" onClick={() => setWrongOpen(true)}>
							<i className="fa-sharp fa-solid fa-circle-exclamation" aria-hidden="true" />
							Wrong lyrics
						</button>
					) : (
						<div className="ld-lyrics__wrong" role="group" aria-label="Wrong lyrics">
							<p className="ld-lyrics__wronghead">Wrong words? Replace them:</p>
							<div className="ld-lyrics__acts">
								<button
									type="button"
									className="ld-lyrics__btn"
									disabled={lyrics?.isLoading}
									onClick={() => {
										if (lyrics?.isLoading) return;
										setWrongOpen(false);
										lyrics?.searchAgain?.();
									}}>
									<i className="fa-sharp fa-solid fa-magnifying-glass" aria-hidden="true" />
									Search again
								</button>
								<button
									type="button"
									className="ld-lyrics__btn"
									onClick={() => {
										setWrongOpen(false);
										lyrics?.pasteRight?.();
									}}>
									<i className="fa-sharp fa-solid fa-paste" aria-hidden="true" />
									Paste the right words
								</button>
								<button type="button" className="ld-lyrics__btn ld-lyrics__btn--quiet" onClick={() => setWrongOpen(false)}>
									Cancel
								</button>
							</div>
						</div>
					)}
				</div>
			)}
			{hasBar && (
				<NowPlayingBar
					song={song}
					file={nowPlaying.file}
					files={nowPlaying.files || []}
					player={nowPlaying.player}
					onSaveKey={nowPlaying.onSaveKey || null}
					saveKeyWho={nowPlaying.saveKeyWho || null}>
					{jotField}
				</NowPlayingBar>
			)}
			{!hasBar && jotField && <div className="ld-lyrics__jotdock">{jotField}</div>}
		</div>
	);

	if (detached) {
		return (
			<>
				<div className="ld-lyrics__detached" role="status">
					<i className="fa-sharp fa-solid fa-window-restore" aria-hidden="true" />
					<span className="ld-lyrics__detachedtext">{hasBar ? 'Now Playing is in its own window.' : 'The lyrics are in their own window.'}</span>
					<button type="button" className="ld-lyrics__btn" onClick={detach.onAttach}>
						{hasBar ? 'Bring it back' : 'Bring them back'}
					</button>
				</div>
				<PopoutWindow
					path={LYRICS_WINDOW_PATH}
					name={LYRICS_WINDOW_NAME}
					onClosed={detach.onAttach}
					onBlocked={() => {
						setBlocked(true);
						detach.onAttach();
					}}>
					{pane}
				</PopoutWindow>
			</>
		);
	}
	return pane;
};

export default LyricsPane;
